import classNames from "classnames";
import { useRouter } from "next/router";
import React, { useCallback, useContext, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import IconCross from "../../../public/svg/icon-cross.svg";
import { CartContext } from "../../contexts/CartContext";
import { ThemeContext } from "../../contexts/ThemeContext";
import { storeSeasonCardInSessionStorage } from "../../ecommerce/cart";
import {
  getSeasonCardHolderName,
  getSeasonCardVariantTitle,
} from "../../ecommerce/common";
import { ProductType } from "../../ecommerce/product";
import { CartItem } from "../../types/shopify-components";
import LocaleProductLink from "../LocaleProductLink";
import Money from "../Money";

import EcommerceProductImage from "./EcommerceProductImage";
import ProductVariantName from "./ProductVariantName";
import QuantityControl from "./QuantityControl";

export const TEST_ID_ITEM = "cart-line-item";
export const TEST_ID_TITLE = "cart-line-item-title";
export const TEST_ID_COMPARE_AT_PRICE = "cart-line-item-original-price";
export const TEST_ID_COMPARE_AT_UNIT_PRICE =
  "cart-line-item-original-unit-price";
export const TEST_ID_PRICE = "cart-line-item-price";
export const TEST_ID_UNIT_PRICE = "cart-line-item-unit-price";
export const TEST_ID_REMOVE = "cart-line-item-remove";
export const TEST_ID_SEASON_PASS_MEDIA = "cart-line-item-season-pass-media";

interface CartLineProps {
  connectedItems?: CartItem[];
  isPastItem: boolean;
  item: CartItem;
}

const CartLineItem: React.FC<CartLineProps> = ({
  connectedItems,
  isPastItem,
  item,
}) => {
  const quantity = item.input.quantity ?? 0;
  const {
    compareAtPrice: compateAtPriceRaw,
    handle,
    image,
    maximumHeight,
    minimumHeight,
    price: priceRaw,
    selectedOptions,
    validityDate,
    tags,
    title,
    weight,
  } = item.merchandise;
  const router = useRouter();
  const { locale, formatMessage } = useIntl();
  const { styles } = useContext(ThemeContext);
  const { remove, isAddingLines, isRemovingLines } = useContext(CartContext);

  const allItems = useMemo(() => {
    return connectedItems ? [item, ...connectedItems] : [item];
  }, [connectedItems, item]);

  const handleLineItemRemove = useCallback((): void => {
    remove(allItems);
  }, [allItems, remove]);

  const compareAtPrice = compateAtPriceRaw
    ? Number(compateAtPriceRaw.amount)
    : undefined;
  const price = Number(priceRaw.amount);
  const isCompareAtPriceVisible =
    compareAtPrice !== undefined && compareAtPrice > price;

  const redirectToEdit = useCallback(() => {
    storeSeasonCardInSessionStorage(item);
    router.push(`/${locale}/product/${item.merchandise.handle}`);
  }, [item, locale, router]);

  const shipByMailOnly = weight !== undefined && weight !== null && weight >= 1;
  const pastItemClassNames = classNames(
    "ba b--red bw2",
    isCompareAtPriceVisible ? "ph2" : "pa2",
  );

  return (
    <div
      className={classNames(
        "flex flex-wrap flex-nowrap-l items-center justify-between",
        isPastItem && pastItemClassNames,
      )}
      data-testid={TEST_ID_ITEM}
    >
      <div className="order-3 w4 order-initial-l mr3">
        <div className="aspect-ratio aspect-ratio--1x1">
          <EcommerceProductImage imageSrc={image?.url} productName={title} />
        </div>
      </div>
      {item.merchandise.productType === ProductType.SEASON_CARD ? (
        <div className="order-0 w-90 order-initial-l w-75-l pr3">
          <LocaleProductLink handle={handle}>
            <a className="db color-inherit">
              <h2
                className={`${styles.paragraph1} ma0`}
                data-testid={TEST_ID_TITLE}
              >
                {title}
              </h2>
            </a>
          </LocaleProductLink>
          <p
            className={`${styles.paragraph3} ma0`}
            data-testid={TEST_ID_SEASON_PASS_MEDIA}
          >
            {getSeasonCardVariantTitle(selectedOptions)}
          </p>
          <p className={`${styles.paragraph3} ma0`}>
            {getSeasonCardHolderName(
              item.input.attributes ?? [],
              item.merchandise.productType,
            )}
          </p>
          <button
            className={`${styles.textButton} ${styles.paragraph3} pa0`}
            onClick={redirectToEdit}
          >
            <FormattedMessage id="seasonpass.editInfo" />
          </button>
        </div>
      ) : (
        <>
          <div className="order-0 w-90 order-initial-l w-50-l pr3">
            <LocaleProductLink handle={handle}>
              <a className="db color-inherit">
                <h2
                  className={`${styles.paragraph1} ma0`}
                  data-testid={TEST_ID_TITLE}
                >
                  {title}
                </h2>
              </a>
            </LocaleProductLink>
            <ProductVariantName
              validityDate={validityDate}
              minimumHeight={minimumHeight}
              maximumHeight={maximumHeight}
              tags={tags}
            />
            {shipByMailOnly && (
              <div className={styles.paragraph3}>
                <FormattedMessage id="product.shippableOnlyByMail" />
              </div>
            )}
          </div>
          <div className="order-4 order-initial-l w-25-l">
            <QuantityControl
              allowZero={false}
              quantity={quantity}
              merchandise={item.merchandise}
              connectedMerchandises={connectedItems?.map(
                item => item.merchandise,
              )}
              updateOnChange={true}
            />
          </div>
        </>
      )}

      <div className="order-2 w-100 mv3 order-initial-l ma0 w-40-l tr-l">
        <table>
          <thead>
            <tr>
              <th className="nowrap tr pr3">
                <FormattedMessage id="lineItemTotal.unitPrice" />
              </th>
              <th className="nowrap tr">
                <FormattedMessage id="lineItemTotal.totalPrice" />
              </th>
            </tr>
          </thead>
          <tbody>
            {isCompareAtPriceVisible && (
              <tr>
                <td
                  className={`${styles.heading3} nowrap dark-gray strike tr pr3`}
                  data-testid={TEST_ID_COMPARE_AT_UNIT_PRICE}
                >
                  <Money value={compareAtPrice} />
                </td>
                <td
                  className={`${styles.heading3} nowrap dark-gray strike tr`}
                  data-testid={TEST_ID_COMPARE_AT_PRICE}
                >
                  <Money value={compareAtPrice * quantity} />
                </td>
              </tr>
            )}
            <tr>
              <td
                className={`${styles.heading3} nowrap tr pr3`}
                data-testid={TEST_ID_UNIT_PRICE}
              >
                <Money value={price} />
              </td>
              <td
                className={`${styles.heading3} nowrap tr`}
                data-testid={TEST_ID_PRICE}
              >
                <Money value={price * quantity} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="order-1 w-10 order-initial-l w4-l tr">
        <button
          aria-label={formatMessage({ id: "product.removeFromCart" })}
          className="svg-icon black stroke-current-color pa2 bg-transparent bw0 pointer hover-green f0"
          disabled={isAddingLines || isRemovingLines}
          onClick={handleLineItemRemove}
          data-testid={TEST_ID_REMOVE}
        >
          <IconCross />
        </button>
      </div>
    </div>
  );
};

export default CartLineItem;
